import { z } from 'zod';

// =============================================================================
//  Plan schemas — Story 2.5a (Backend + Lista)
//  Planos comerciais que combinam 0..N entitlements de SaaS produtos.
//  Wizard 3 passos e history (price/entitlements) ficam pra Story 2.5b.
// =============================================================================

export const planTypeSchema = z.enum(['single_saas', 'bundle', 'service_only']);
export type PlanTypeSchema = z.infer<typeof planTypeSchema>;

export const billingCycleSchema = z.enum([
  'mensal',
  'trimestral',
  'semestral',
  'anual',
  'pagamento_unico',
]);
export type BillingCycleSchema = z.infer<typeof billingCycleSchema>;

export const planStatusSchema = z.enum(['ativo', 'oculto', 'legado', 'descontinuado']);
export type PlanStatusSchema = z.infer<typeof planStatusSchema>;

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

const SLUG_REGEX = /^[a-z0-9_-]+$/;
const slugField = z
  .string()
  .min(1, 'Slug obrigatório.')
  .max(64, 'Slug deve ter até 64 caracteres.')
  .regex(SLUG_REGEX, 'Slug aceita só minúsculas, números, hífen e underscore.');

const optionalText = (maxLen: number, label: string) =>
  z.string().max(maxLen, `${label} deve ter até ${maxLen} caracteres.`).optional().nullable();

const jsonRecord = z.record(z.unknown()).optional().nullable();

const checkoutHighlightsField = z
  .array(z.string())
  .transform((items) => items.map((s) => s.trim()).filter((s) => s.length > 0))
  .pipe(
    z
      .array(z.string().max(120, 'Cada destaque deve ter até 120 caracteres.'))
      .max(6, 'Máximo de 6 destaques no checkout.'),
  );

const nonNegDecimal = (label: string) =>
  z.coerce
    .number({ invalid_type_error: `${label} deve ser numérico.` })
    .nonnegative(`${label} deve ser >= 0.`);

const positiveOptionalDecimal = (label: string) =>
  z.coerce
    .number({ invalid_type_error: `${label} deve ser numérico.` })
    .positive(`${label} deve ser > 0.`)
    .optional()
    .nullable();

// -----------------------------------------------------------------------------
// Entitlement (subset usado em CREATE — em 2.5a não permite editar via PATCH)
// -----------------------------------------------------------------------------

export const createPlanEntitlementSchema = z.object({
  saas_product_id: z.string().uuid('saas_product_id inválido.'),
  external_plan_id: z.string().uuid('external_plan_id inválido.'),
  limits_override: jsonRecord,
  display_order: z.coerce.number().int().nonnegative().default(0),
});
export type CreatePlanEntitlementDto = z.output<typeof createPlanEntitlementSchema>;
export type CreatePlanEntitlementInput = z.input<typeof createPlanEntitlementSchema>;

// -----------------------------------------------------------------------------
// Base mutável SEM defaults (defaults só fazem sentido em CREATE).
// Isso evita conflitos entre `.default()` e `.partial()` no UPDATE.
// -----------------------------------------------------------------------------

const planMutableBase = z.object({
  name: z.string().min(1, 'Nome obrigatório.').max(200, 'Nome deve ter até 200 caracteres.'),
  description: optionalText(5000, 'Descrição'),
  type: planTypeSchema,
  monthly_price: nonNegDecimal('Preço mensal'),
  annual_price: positiveOptionalDecimal('Preço anual'),
  setup_fee: nonNegDecimal('Setup fee'),
  promo_price: positiveOptionalDecimal('Preço promocional'),
  billing_cycle: billingCycleSchema,
  discount_annual_pct: z.coerce
    .number()
    .min(0, 'Desconto >= 0.')
    .max(100, 'Desconto <= 100.')
    .optional()
    .nullable(),
  trial_days: z.coerce.number().int().nonnegative('trial_days >= 0.'),
  requires_card_for_trial: z.coerce.boolean(),
  blocks_repeat_trial: z.coerce.boolean(),
  limits: jsonRecord,
  features: jsonRecord,
  // Destaques do resumo do checkout público (um por linha na UI). Fora de
  // `features` de propósito — features é contrato de entitlements dos SaaS.
  // Linhas vazias são descartadas ANTES da validação (a textarea manda o
  // rascunho cru, com \n pendente, e isso não pode virar erro de submit).
  checkout_highlights: checkoutHighlightsField,
  status: planStatusSchema,
  // Fase 2 — NFS-e auto (DR-036)
  auto_emit_nf: z.coerce.boolean(),
  service_description: z
    .string()
    .min(10, 'Descrição do serviço precisa de pelo menos 10 caracteres.')
    .max(500, 'Descrição do serviço deve ter até 500 caracteres.')
    .optional()
    .nullable(),
});

// Cross-field validation: auto_emit_nf=true exige service_description preenchido.
// Aplicado em CREATE e UPDATE separadamente porque base não suporta superRefine herdado.
const autoEmitNfRequiresDescription = (
  data: { auto_emit_nf?: boolean; service_description?: string | null },
  ctx: z.RefinementCtx,
): void => {
  if (data.auto_emit_nf === true) {
    const desc = data.service_description?.trim();
    if (!desc) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['service_description'],
        message: 'Descrição do serviço obrigatória quando auto_emit_nf=true (DR-036).',
      });
    }
  }
};

// -----------------------------------------------------------------------------
// CREATE — sobrescreve fields com defaults; exige slug e entitlements;
// valida type × N entitlements via superRefine.
// -----------------------------------------------------------------------------

export const createPlanSchema = planMutableBase
  .extend({
    slug: slugField,
    type: planTypeSchema.default('single_saas'),
    monthly_price: nonNegDecimal('Preço mensal').default(0),
    setup_fee: nonNegDecimal('Setup fee').default(0),
    billing_cycle: billingCycleSchema.default('mensal'),
    trial_days: z.coerce.number().int().nonnegative('trial_days >= 0.').default(0),
    requires_card_for_trial: z.coerce.boolean().default(false),
    blocks_repeat_trial: z.coerce.boolean().default(true),
    status: planStatusSchema.default('ativo'),
    auto_emit_nf: z.coerce.boolean().default(false),
    checkout_highlights: z.array(z.string()).default([]).pipe(checkoutHighlightsField),
    entitlements: z
      .array(createPlanEntitlementSchema)
      .max(20, 'Máximo de 20 entitlements por plano.'),
  })
  .superRefine((data, ctx) => {
    autoEmitNfRequiresDescription(data, ctx);
    const n = data.entitlements.length;
    if (data.type === 'service_only' && n !== 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['entitlements'],
        message: 'type=service_only deve ter 0 entitlements.',
      });
    }
    if (data.type === 'single_saas' && n !== 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['entitlements'],
        message: 'type=single_saas deve ter exatamente 1 entitlement.',
      });
    }
    if (data.type === 'bundle' && n < 2) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['entitlements'],
        message: 'type=bundle deve ter 2+ entitlements.',
      });
    }
    const saasIds = data.entitlements.map((e) => e.saas_product_id);
    const dup = saasIds.find((id, i) => saasIds.indexOf(id) !== i);
    if (dup) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['entitlements'],
        message: 'Mesmo SaaS Product aparece 2× — escolha planos de SaaS diferentes.',
      });
    }
  });
export type CreatePlanDto = z.output<typeof createPlanSchema>;
export type CreatePlanInput = z.input<typeof createPlanSchema>;

// -----------------------------------------------------------------------------
// EDIT FORM — usado pelo PlanForm em editMode. Mesmos fields do create, MAS sem
// a regra type × N entitlements: em edição os entitlements são read-only (form
// manda []) e o PATCH os descarta. Sem este schema, editar um plano single_saas
// falhava silenciosamente na validação (entitlements=[] ≠ 1) sem feedback —
// o famoso "botão não faz nada".
// -----------------------------------------------------------------------------

export const editPlanFormSchema = createPlanSchema
  .innerType()
  .extend({
    entitlements: z.array(createPlanEntitlementSchema).max(20).default([]),
  })
  .superRefine(autoEmitNfRequiresDescription);
export type EditPlanFormDto = z.output<typeof editPlanFormSchema>;

// -----------------------------------------------------------------------------
// UPDATE — partial sobre o base + version obrigatória.
// `slug` é imutável após criação. `entitlements` opcional — quando presente,
// service faz diff vs existentes (replace pattern: insert/update/delete)
// dentro de $transaction com history writes (Story 2.5b).
// -----------------------------------------------------------------------------

export const updatePlanSchema = planMutableBase
  .partial()
  .extend({
    version: z.coerce.number().int().positive('version obrigatório (optimistic locking).'),
    entitlements: z
      .array(createPlanEntitlementSchema)
      .max(20, 'Máximo de 20 entitlements por plano.')
      .optional(),
  })
  .strict()
  .superRefine((data, ctx) => {
    if (data.auto_emit_nf !== undefined || data.service_description !== undefined) {
      autoEmitNfRequiresDescription(data, ctx);
    }
  })
  .refine((data) => !('slug' in data), {
    message: 'Slug é imutável após criação.',
    path: ['slug'],
  });
export type UpdatePlanDto = z.output<typeof updatePlanSchema>;
export type UpdatePlanInput = z.input<typeof updatePlanSchema>;

// -----------------------------------------------------------------------------
// DELETE — exige version (vem do header If-Match ou body, igual SaaS Products)
// -----------------------------------------------------------------------------

export const deletePlanSchema = z.object({
  version: z.coerce.number().int().positive('version obrigatório (optimistic locking).'),
});
export type DeletePlanDto = z.infer<typeof deletePlanSchema>;

// -----------------------------------------------------------------------------
// QUERY — GET /api/plans
// -----------------------------------------------------------------------------

export const queryPlansSchema = z.object({
  type: planTypeSchema.optional(),
  status: planStatusSchema.optional(),
  q: z.string().min(1).max(200).optional(),
  cursor: z.string().uuid().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type QueryPlansDto = z.infer<typeof queryPlansSchema>;
